import React from 'react';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import FormHelperText from '@mui/material/FormHelperText';
import FormControl from '@mui/material/FormControl';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/material/styles/useTheme';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import { isEmbedded } from '../../../../util';

type DateSetter = (date: Dayjs) => void;

type PricingDatePickerProps = {
  effectiveDate: Dayjs;
  effectiveDateError?: string | null;
  expirationDate: Dayjs;
  expirationDateError?: string | null;
  setEffectiveDate: DateSetter;
  setExpirationDate: DateSetter;
};

const PricingDatePicker = (props: PricingDatePickerProps) => {
  const translate = useTranslate();
  const localization = useLocalizationContext();

  return (
    <LocalizationProvider
      adapterLocale={localization.locale}
      dateAdapter={AdapterDayjs}
    >
      <Typography fontWeight={'bold'} mb={1} mt={3} variant="body2">
        {translate('Pricing.RuleTiming')}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        <DatePicker
          error={props.effectiveDateError}
          id="effective-date"
          label={translate('Pricing.EffectiveDate')}
          minDate={dayjs()}
          onChange={props.setEffectiveDate}
          value={props.effectiveDate}
        />
        <DatePicker
          error={props.expirationDateError}
          id="expiration-date"
          label={translate('Pricing.ExpirationDate')}
          minDate={props.effectiveDate}
          onChange={props.setExpirationDate}
          value={props.expirationDate}
        />
      </Stack>
    </LocalizationProvider>
  );
};

const DatePicker = (props: any) => {
  const theme = useTheme();
  const isMediumUp = useMediaQuery(theme.breakpoints.up('sm'));

  return (
    <FormControl
      data-testid={props.id}
      error={Boolean(props.error)}
      id={props.id}
    >
      <Typography
        component={'label'}
        htmlFor={`${props.id}-picker-input`}
        variant="body2"
      >
        {props.label}
      </Typography>
      <MuiDatePicker
        InputProps={{
          id: `${props.id}-picker-input`,
          sx: {
            '& .MuiInputBase-input': { px: 1.2, py: 1 },
            '& fieldset': { borderColor: props.error ? '#B11616' : '#8B8B8B' },
            width: 250,
          },
        }}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={!(isEmbedded() && isMediumUp)}
        components={{
          OpenPickerIcon: CalendarMonthOutlinedIcon,
        }}
        desktopModeMediaQuery={theme.breakpoints.up('sm')}
        disablePast
        inputFormat="MM/DD/YYYY"
        maxDate={props.maxDate}
        minDate={props.minDate}
        onChange={props.onChange}
        renderInput={(params: any) => <TextField {...params} />}
        value={props.value}
      />
      <FormHelperText sx={{ ml: 0 }}>{props.error}</FormHelperText>
    </FormControl>
  );
};

export default PricingDatePicker;
